/**
 * Session expired interstitial (TMMAPP-0003). Rendered at `/auth/session-expired` and in place
 * by `AuthGate` after a runtime expiry. Counts down from 3 and redirects to centralized login
 * ONCE — the button fires the same single-shot redirect immediately.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { redirectToCentralizedLogin } from '@/auth/silentRefresh';
import { StandaloneFrame } from '@/shell/StandaloneFrame';

const COUNTDOWN_SECONDS = 3;

export function SessionExpired() {
  const { t } = useTranslation();
  const location = useLocation();
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const redirected = useRef(false);
  const returnPath = `${location.pathname}${location.search}`;

  const redirect = useCallback(() => {
    if (redirected.current) return;
    redirected.current = true;
    redirectToCentralizedLogin(returnPath);
  }, [returnPath]);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (seconds === 0) redirect();
  }, [seconds, redirect]);

  return (
    <StandaloneFrame>
      <Typography variant="h1" gutterBottom ref={headingRef} tabIndex={-1}>
        {t('auth.sessionExpiredTitle')}
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        {t('auth.sessionExpiredBody')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }} role="status">
        {t('auth.redirectingIn', { seconds })}
      </Typography>
      <Button variant="contained" onClick={redirect}>
        {t('auth.signInNow')}
      </Button>
    </StandaloneFrame>
  );
}
